/**
 * La consulta de las listas de Operaciones, como OPCIONES reutilizables.
 *
 * ⭐ Existe para que la RUTA y la PANTALLA compartan exactamente la misma definición (ADR-0012,
 * paso 2). El `loader` arranca la carga cuando arranca la navegación —antes de que el componente
 * exista— y la pantalla, al montar, encuentra el dato ya puesto.
 *
 * 🪤 Si la clave o el `queryFn` se escribieran dos veces, el loader calentaría una entrada que la
 * pantalla nunca lee: el usuario paga la consulta dos veces y no gana nada. Ya pasó con la precarga
 * del Inicio, y por eso Productos y esto comparten el mismo patrón.
 *
 * 🪤 Vive en su propio archivo y NO dentro de `OperationsOrdersView.tsx`: el loader lo importa de
 * forma estática, y arrastrar la vista (1.300 líneas de UI) al arranque anularía su carga diferida.
 * Acá sólo entra código de datos.
 */

import {
  fetchOrdersPage,
  type OrdersPageParams,
} from '@/lib/operations/ordersApi';

/**
 * Los mismos parámetros que pide `fetchOrdersPage`, con `pageSize` en vez de `limit`.
 *
 * 🪤 Se DERIVAN del tipo de la API en vez de reescribirse: si mañana `fetchOrdersPage` cambia una
 * etapa o agrega un filtro, esto deja de compilar en vez de aceptar un `string` que la BD rechaza
 * en runtime. Reescribir el tipo a mano fue el primer intento y el typecheck lo rebotó.
 */
export type ParametrosPedidosOps = Omit<OrdersPageParams, 'limit'> & { pageSize: number };

export function opcionesPedidosOps(p: ParametrosPedidosOps) {
  return {
    // La clave ES la identidad de la consulta: todo criterio que cambie el resultado va acá.
    queryKey: ['ops-orders', p.tenantId, p.stages.join(','), p.search,
      (p.paymentStatus ?? []).join(','), (p.shippingStatus ?? []).join(','), (p.channel ?? []).join(','),
      (p.billingStatus ?? []).join(','), p.onlyWithDue ?? false, p.sort, p.offset, p.pageSize],
    staleTime: 30_000,
    queryFn: () => fetchOrdersPage({
      tenantId: p.tenantId,
      stages: p.stages,
      search: p.search,
      paymentStatus: p.paymentStatus,
      shippingStatus: p.shippingStatus,
      channel: p.channel,
      billingStatus: p.billingStatus,
      onlyWithDue: p.onlyWithDue,
      sort: p.sort,
      offset: p.offset,
      limit: p.pageSize,
    }),
  };
}
